<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Dynamic Sidebar -->
    <component :is="currentLayout.sidebar" v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Dynamic Topbar (Sticky) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <component 
          :is="currentLayout.topbar"
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- ============================================ -->
        <!-- HOD VIEW: Department Leave Management -->
        <!-- ============================================ -->
        <template v-if="isHOD">
          
          <!-- HOD Dashboard View -->
          <template v-if="currentView === 'dashboard'">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Department Leave Management</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage leave requests for {{ userDepartment }} - {{ userSection }}</p>
              </div>
              <button @click="openNewRequestModal" class="inline-flex items-center justify-center px-5 py-2.5 bg-[#035a76] hover:bg-[#024a61] text-white text-sm font-medium rounded-lg shadow-sm transition-colors">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                New Leave Request
              </button>
            </div>

            <!-- Summary Cards -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div @click="currentView = 'staff-on-leave'" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 transition-all cursor-pointer group">
                <div class="flex justify-between items-start">
                  <div>
                    <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Staff on Leave</p>
                    <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2 group-hover:text-blue-600 transition-colors">{{ totalStaffOnLeave }}</p>
                  </div>
                  <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                  </div>
                </div>
              </div>
              <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Teaching Staff on Leave</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ teachingStaffOnLeave }}</p>
              </div>
              <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Non-Teaching on Leave</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ nonTeachingStaffOnLeave }}</p>
              </div>
<div 
  @click="currentView = 'pending-requests'"
  class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-amber-300 dark:hover:border-amber-700 transition-all cursor-pointer group"
>
  <div class="flex justify-between items-start">
    <div>
      <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Requests</p>
      <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">{{ pendingLeaveRequests }}</p>
    </div>
    <div class="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">
      <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  </div>
  <div class="flex items-center justify-between mt-4">
    <p class="text-xs text-gray-500 dark:text-gray-400">Click to review & approve</p>
    <svg class="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
  </div>
</div>
            </div>

            <!-- Employee Search & Management -->
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Find Employee in Department</h3>
              <div class="relative mb-6">
                <input v-model="employeeSearch" type="text" placeholder="Search by Staff ID or Name..." class="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76]">
                <svg class="absolute left-3 top-3 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <ul v-if="employeeSearch.trim().length >= 2" class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-auto">
                  <li v-for="emp in searchResults" :key="emp.applicant_id" @click="selectEmployee(emp)" class="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <div class="flex justify-between">
                      <span class="font-medium text-gray-900 dark:text-white">{{ emp.full_name || emp.first_name + ' ' + emp.last_name }}</span>
                      <span class="text-gray-500 dark:text-gray-400 text-xs">{{ emp.position?.position_title || 'N/A' }}</span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">ID: {{ emp.employee_code }} • {{ emp.section?.section_name }}</div>
                  </li>
                  <li v-if="searchResults.length === 0" class="p-3 text-sm text-gray-500 text-center">No employees found.</li an>
                </ul>
              </div>

              <!-- Selected Employee Details -->
              <div v-if="selectedEmployee" class="space-y-6 animate-fade-in">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div><p class="text-xs font-semibold text-gray-500 uppercase">Name</p><p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedEmployeeName }}</p></div>
                  <div><p class="text-xs font-semibold text-gray-500 uppercase">Position</p><p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedEmployee.position?.position_title || 'N/A' }}</p></div>
                  <div><p class="text-xs font-semibold text-gray-500 uppercase">Staff ID</p><p class="mt-1 font-mono font-medium text-gray-900 dark:text-white">{{ selectedEmployee.employee_code }}</p></div>
                  <div><p class="text-xs font-semibold text-gray-500 uppercase">Section</p><p class="mt-1 font-medium text-gray-900 dark:text-white capitalize">{{ selectedEmployee.section?.section_name || 'N/A' }}</p></div>
                </div>

                <!-- Leave Balances for Selected Employee -->
                <div>
                  <h4 class="text-md font-semibold text-gray-900 dark:text-white mb-3">Leave Balance</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div v-for="(balance, type) in calculatedBalances" :key="type" class="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                      <p class="text-xs font-semibold text-gray-500 uppercase">{{ type }}</p>
                      <p class="text-2xl font-bold text-gray-900 dark:text-white mt-1">{{ balance.remaining }}<span class="text-sm text-gray-400 font-normal">/{{ balance.total }}</span></p>
                    </div>
                  </div>
                </div>

                <!-- Leave History for Selected Employee -->
                <div>
                  <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
                    <h4 class="text-md font-semibold text-gray-900 dark:text-white">Leave History</h4>
                    <select v-model="leaveStatusFilter" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div class="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead class="bg-gray-50 dark:bg-gray-800/50">
                        <tr>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Dates</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Days</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                          <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        <tr v-for="request in displayedLeaveHistory" :key="request.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td class="px-6 py-4 whitespace-nowrap"><span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(request.type) }">{{ request.type }}</span></td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(request.startDate) }} - {{ formatDate(request.endDate) }}</td>
                          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ request.duration }}</td>
                          <td class="px-6 py-4 whitespace-nowrap"><span :class="getStatusClass(request.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize">{{ request.status }}</span></td>
                          <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button @click="viewLeaveDetails(request)" class="text-[#035a76] dark:text-blue-400 hover:underline mr-3">View</button>
                          </td>
                        </tr>
                        <tr v-if="displayedLeaveHistory.length === 0"><td colspan="5" class="px-6 py-12 text-center text-sm text-gray-500">No leave requests found.</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- HOD Staff on Leave Detailed View -->
          <template v-else-if="currentView === 'staff-on-leave'">
            <div class="flex items-center gap-3 mb-6">
              <button @click="currentView = 'dashboard'" class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Staff Currently on Leave</h2>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Employee</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Position</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Leave Type</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Duration</th>
                    <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="staff in activeLeaves" :key="staff.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <div class="h-8 w-8 rounded-full bg-[#035a76] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{{ getInitials(staff.employeeName) }}</div>
                        <div class="ml-3"><div class="text-sm font-medium text-gray-900 dark:text-white">{{ staff.employeeName }}</div><div class="text-xs text-gray-500">{{ staff.employeeCode }}</div></div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ staff.position }}</td>
                    <td class="px-6 py-4 whitespace-nowrap"><span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(staff.type) }">{{ staff.type }}</span></td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(staff.startDate) }} – {{ formatDate(staff.endDate) }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button @click="viewLeaveDetails(staff)" class="text-[#035a76] dark:text-blue-400 hover:underline">View Details</button>
                    </td>
                  </tr>
                  <tr v-if="activeLeaves.length === 0"><td colspan="5" class="px-6 py-12 text-center text-sm text-gray-500">No staff currently on leave.</td></tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- ============================================ -->
<!-- HOD PENDING REQUESTS VIEW -->
<!-- ============================================ -->
<template v-else-if="currentView === 'pending-requests'">
  <div class="flex items-center gap-3 mb-6">
    <button 
      @click="currentView = 'dashboard'"
      class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
    >
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
    </button>
    <div>
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Pending Leave Approvals</h2>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {{ hodPendingRequests.length }} request{{ hodPendingRequests.length !== 1 ? 's' : '' }} awaiting your approval
      </p>
    </div>
  </div>

  <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Employee</th>
            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Leave Type</th>
            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</th>
            <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Submitted</th>
            <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="request in hodPendingRequests" :key="request.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center">
                <div class="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {{ getInitials(request.employeeName) }}
                </div>
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900 dark:text-white">{{ request.employeeName }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">{{ request.employeeCode }}</div>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(request.type) }">
                {{ request.type }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
              {{ formatDate(request.startDate) }} – {{ formatDate(request.endDate) }}
              <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ request.duration }} day{{ request.duration !== 1 ? 's' : '' }}</span>
            </td>
            <td class="px-6 py-4 max-w-xs">
              <p class="text-sm text-gray-600 dark:text-gray-300 truncate" :title="request.reason">{{ request.reason }}</p>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
              {{ formatDate(request.submittedOn) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <div class="flex items-center justify-end gap-2">
                <button 
                  @click="viewLeaveDetails(request)"
                  class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  View
                </button>
                <button 
                  @click="updateLeaveStatus(request.id, 'rejected')"
                  class="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Reject
                </button>
                <button 
                  @click="updateLeaveStatus(request.id, 'approved')"
                  class="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  Approve
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="hodPendingRequests.length === 0">
            <td colspan="6" class="px-6 py-16 text-center">
              <svg class="mx-auto h-12 w-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="mt-2 text-sm font-medium text-gray-900 dark:text-white">All caught up!</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">No pending requests awaiting your approval.</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
        </template>

        <!-- ============================================ -->
        <!-- TEACHER VIEW: My Leave Requests (Original) -->
        <!-- ============================================ -->
        <template v-else>
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white">My Leave Requests</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your time off and track request status</p>
            </div>
            <button @click="openNewRequestModal" class="inline-flex items-center justify-center px-5 py-2.5 bg-[#035a76] hover:bg-[#024a61] text-white text-sm font-medium rounded-lg shadow-sm transition-colors">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
              New Leave Request
            </button>
          </div>

          <!-- Leave Balance Summary -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div v-for="(balance, type) in leaveBalances" :key="type" class="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
              <div class="flex justify-between items-start">
                <div>
                  <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ type }} Leave</p>
                  <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ balance.remaining }}<span class="text-sm text-gray-400 font-normal"> / {{ balance.total }}</span></p>
                </div>
                <div class="p-2 rounded-lg" :class="balance.remaining > 0 ? 'bg-green-50 dark:bg-green-900/30 text-green-600' : 'bg-red-50 dark:bg-red-900/30 text-red-600'">
                  <svg v-if="balance.remaining > 0" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <svg v-else class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
              </div>
              <div class="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div class="h-1.5 rounded-full transition-all duration-500" :class="balance.remaining > 0 ? 'bg-green-500' : 'bg-red-500'" :style="{ width: `${Math.max(0, (balance.remaining / balance.total) * 100)}%` }"></div>
              </div>
            </div>
          </div>

          <!-- Leave History Section -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Leave History</h3>
              <select v-model="leaveStatusFilter" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Leave Type</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Duration</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Days</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Approver</th>
                    <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="request in displayedLeaveHistory" :key="request.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap"><span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(request.type) }">{{ request.type }}</span></td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(request.startDate) }} – {{ formatDate(request.endDate) }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ request.days }}</td>
                    <td class="px-6 py-4 whitespace-nowrap"><span :class="getStatusClass(request.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize">{{ request.status }}</span></td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ request.approver || '—' }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button @click="viewLeaveDetails(request)" class="text-[#035a76] dark:text-blue-400 hover:text-[#024a61] mr-3">View</button>
                      <button v-if="request.status === 'pending'" @click="cancelRequest(request.id)" class="text-red-600 dark:text-red-400 hover:text-red-800">Cancel</button>
                    </td>
                  </tr>
                  <tr v-if="displayedLeaveHistory.length === 0"><td colspan="6" class="px-6 py-12 text-center text-sm text-gray-500">No leave requests found matching your filters.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

      </main>
    </div>

    <!-- ============================================ -->
    <!-- SHARED MODALS -->
    <!-- ============================================ -->
    
    <!-- New Leave Request Modal -->
    <Transition name="modal">
      <div v-if="showNewRequestModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="showNewRequestModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col max-h-[90vh]">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ isHOD ? 'New Leave Request for Employee' : 'New Leave Request' }}</h3>
            <button @click="showNewRequestModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
          </div>
          <div class="px-6 py-6 space-y-5 overflow-y-auto flex-1">
            
            <!-- Employee Selector (Only for HOD) -->
            <div v-if="isHOD">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Employee *</label>
              <Multiselect
                v-model="newRequest.selectedEmployee"
                :options="mappedDeptEmployees"
                :custom-label="app => `${app.full_name || app.first_name + ' ' + app.last_name} (${app.employee_code})`"
                track-by="applicant_id"
                searchable
                placeholder="Search employee by name or ID..."
                :show-labels="false"
                class="multiselect-custom"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Leave Type *</label>
              <select v-model="newRequest.leave_type_id" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76]">
                <option value="">Select leave type...</option>
                <option v-for="type in leaveTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Date Range *</label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label><input v-model="newRequest.start_date" type="date" @change="calculateDuration" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76]"></div>
                <div><label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label><input v-model="newRequest.end_date" type="date" @change="calculateDuration" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76]"></div>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason for Leave *</label>
              <textarea v-model="newRequest.reason" rows="3" required class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] resize-none" placeholder="Please provide a brief explanation..."></textarea>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <button @click="showNewRequestModal = false" type="button" class="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">Cancel</button>
            <button @click="submitRequest" type="button" :disabled="isSubmitting" class="px-5 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-[#035a76] hover:bg-[#024a61] transition-colors shadow-sm disabled:opacity-50 flex items-center">
              <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              {{ isSubmitting ? 'Submitting...' : 'Submit Request' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Leave Details Modal -->
    <Transition name="modal">
      <div v-if="selectedLeave" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="selectedLeave = null">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Request Details</h3>
            <button @click="selectedLeave = null" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
          </div>
          <div class="px-6 py-6 space-y-4">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Leave Type</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white text-lg">{{ selectedLeave.type }}</p>
                <p v-if="isHOD" class="text-sm text-gray-600 dark:text-gray-400 mt-1">Requested by: {{ selectedLeave.employeeName }}</p>
              </div>
              <span :class="getStatusClass(selectedLeave.status)" class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize">{{ selectedLeave.status }}</span>
            </div>
            <div class="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div><p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Start Date</p><p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDate(selectedLeave.startDate) }}</p></div>
              <div><p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">End Date</p><p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDate(selectedLeave.endDate) }}</p></div>
              <div><p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Days</p><p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedLeave.duration || selectedLeave.days }}</p></div>
              <div><p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Submitted On</p><p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDate(selectedLeave.submittedOn) }}</p></div>
            </div>
            <div class="pt-4 border-t border-gray-100 dark:border-gray-700">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reason</p>
              <p class="mt-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{{ selectedLeave.reason }}</p>
            </div>
          </div>
          
          <!-- Action Buttons based on Role and Status -->
          <div v-if="selectedLeave.status === 'pending'" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50">
            <button v-if="!isHOD" @click="cancelRequest(selectedLeave.id)" class="px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm">Cancel Request</button>
            
            <template v-if="isHOD">
              <button @click="updateLeaveStatus(selectedLeave.id, 'rejected')" class="px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm">Reject</button>
              <button @click="updateLeaveStatus(selectedLeave.id, 'approved')" class="px-4 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors shadow-sm">Approve</button>
                        </template>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import Multiselect from 'vue-multiselect'; // Added for HOD employee selection
import api from '@/services/api';

// Dynamic Component Imports
import AcdSidebar from '@/components/acd/AcdSidebar.vue';
import AcdTopbar from '@/components/acd/AcdTopbar.vue';
import CommSidebar from '@/components/com/ComSidebar.vue';
import CommTopbar from '@/components/com/ComTopbar.vue';
import ItSidebar from '@/components/it/ItSidebar.vue';
import ItTopbar from '@/components/it/ItTopbar.vue';
import AdministrationSidebar from '@/components/administration/AdministrationSidebar.vue';
import AdministrationTopbar from '@/components/administration/AdministrationTopbar.vue';

const route = useRoute();

// Layout Mapping
const layoutMap = {
  academics: { sidebar: AcdSidebar, topbar: AcdTopbar },
  it: { sidebar: ItSidebar, topbar: ItTopbar },
  com: { sidebar: CommSidebar, topbar: CommTopbar },
  administration: { sidebar: AdministrationSidebar, topbar: AdministrationTopbar },
  procurement: { sidebar: AdministrationSidebar, topbar: AdministrationTopbar }
};
const currentModule = computed(() => route.path.startsWith('/academics') ? 'academics' : 'academics');
const currentLayout = computed(() => layoutMap[currentModule.value] || layoutMap.academics);

// State
const isSidebarOpen = ref(false);
const isDarkMode = ref(false);
const currentPageTitle = ref('Leave Management');
const currentView = ref('dashboard'); // For HOD navigation
const leaveStatusFilter = ref('all');
const showNewRequestModal = ref(false);
const selectedLeave = ref(null);
const isSubmitting = ref(false);
const employeeSearch = ref('');

const leaveTypes = ref([]);
const leaveHistory = ref([]); // For Teacher view
const leaveBalances = ref({});

// HOD Specific State
const allLeaves = ref([]);
const allEmployees = ref([]);

const newRequest = ref({
  selectedEmployee: null, // For HOD
  leave_type_id: '',
  start_date: '',
  end_date: '',
  days: 0,
  reason: ''
});

// User Context
const currentUser = computed(() => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) return null;
  try { return JSON.parse(storedUser); } catch { return null; }
});

const currentUserId = computed(() => currentUser.value?.id ? String(currentUser.value.id) : null);
const isHOD = computed(() => currentUser.value?.position?.includes('Head of Department'));
const userDepartment = computed(() => currentUser.value?.department);
const userSection = computed(() => currentUser.value?.section);

// Filtered Employees for HOD
const deptEmployees = computed(() => {
  if (!isHOD.value) return [];
  return allEmployees.value.filter(e => 
    e.department?.department_name === userDepartment.value && 
    e.section?.section_name === userSection.value
  );
});

const mappedDeptEmployees = computed(() => deptEmployees.value);

const searchResults = computed(() => {
  const query = employeeSearch.value.trim().toLowerCase();
  if (query.length < 2) return [];
  return deptEmployees.value.filter(emp => {
    const name = (emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`).toLowerCase();
    const code = (emp.employee_code || '').toLowerCase();
    return name.includes(query) || code.includes(query);
  }).slice(0, 10);
});

const selectedEmployee = ref(null);
const selectedEmployeeName = computed(() => {
  if (!selectedEmployee.value) return '';
  return selectedEmployee.value.full_name || `${selectedEmployee.value.first_name || ''} ${selectedEmployee.value.last_name || ''}`.trim();
});

// Enriched Leaves for HOD (merges leave data with employee details)
const enrichedLeaves = computed(() => {
  return allLeaves.value.map(leave => {
    const emp = deptEmployees.value.find(e => String(e.applicant_id) === String(leave.applicant_id));
    return {
      id: leave.id,
      applicant_id: String(leave.applicant_id),
      employeeName: emp ? (emp.full_name || `${emp.first_name} ${emp.last_name}`) : (leave.employee_name || 'Unknown'),
      employeeCode: emp ? emp.employee_code : (leave.employee_code || 'N/A'),
      position: emp ? (emp.position?.position_title || 'N/A') : 'N/A',
      department: emp ? (emp.department?.department_name || 'N/A') : (leave.department || 'N/A'),
      section: emp ? (emp.section?.section_name || 'N/A') : (leave.section || 'N/A'),
      userType: emp ? (emp.user_type || 'N/A') : 'N/A',
      type: leave.leave_type_name,
      startDate: leave.start_date,
      endDate: leave.end_date,
      duration: calculateDays(leave.start_date, leave.end_date),
      status: leave.status,
      reason: leave.reason,
      approver: leave.approved_by,
      submittedOn: leave.created_at
    };
  });
});

// HOD Stats
const today = new Date().toISOString().split('T')[0];
const activeLeaves = computed(() => enrichedLeaves.value.filter(l => l.status === 'approved' && l.startDate <= today && l.endDate >= today));
const totalStaffOnLeave = computed(() => activeLeaves.value.length);
const teachingStaffOnLeave = computed(() => activeLeaves.value.filter(l => l.userType === 'Teacher').length);
const nonTeachingStaffOnLeave = computed(() => activeLeaves.value.filter(l => l.userType !== 'Teacher').length);
const pendingLeaveRequests = computed(() => enrichedLeaves.value.filter(l => l.status === 'pending').length);

// Unified Leave History Display
const displayedLeaveHistory = computed(() => {
  if (isHOD.value) {
    if (!selectedEmployee.value) return [];
    const empId = String(selectedEmployee.value.applicant_id);
    let filtered = enrichedLeaves.value.filter(leave => String(leave.applicant_id) === empId);
    if (leaveStatusFilter.value !== 'all') filtered = filtered.filter(l => l.status === leaveStatusFilter.value);
    return filtered.sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn));
  } else {
    let filtered = leaveHistory.value;
    if (leaveStatusFilter.value !== 'all') filtered = filtered.filter(l => l.status === leaveStatusFilter.value);
    return filtered;
  }
});

// Balances Calculation
const calculatedBalances = computed(() => {
  if (!isHOD.value || !selectedEmployee.value) return {};
  const balances = {};
  const empId = String(selectedEmployee.value.applicant_id);
  leaveTypes.value.forEach(type => {
    const total = type.default_days || 21; 
    const used = enrichedLeaves.value
      .filter(l => String(l.applicant_id) === empId && l.type === type.name && l.status === 'approved')
      .reduce((sum, l) => sum + l.duration, 0);
    balances[type.name] = { total, remaining: Math.max(0, total - used) };
  });
  return balances;
});

// Data Fetching
const fetchData = async () => {
  try {
    const [typesRes, leavesRes, empRes] = await Promise.all([
      api.get('LeaveTypes.php'),
      isHOD.value 
        ? api.get(`EmployeeLeaves.php?department=${encodeURIComponent(userDepartment.value)}&section=${encodeURIComponent(userSection.value)}`)
        : api.get(`EmployeeLeaves.php?user_id=${currentUserId.value}`),
      isHOD.value ? api.get('get_hired_applicants.php') : Promise.resolve({ data: { data: [] } })
    ]);

    leaveTypes.value = typesRes.data || [];
    
    if (isHOD.value) {
      allLeaves.value = leavesRes.data?.data || [];
      allEmployees.value = empRes.data?.data || [];
    } else {
      leaveHistory.value = (leavesRes.data?.data || []).map(leave => ({
        id: leave.id,
        type: leave.leave_type_name,
        startDate: leave.start_date,
        endDate: leave.end_date,
        days: calculateDays(leave.start_date, leave.end_date),
        status: leave.status,
        approver: (leave.approved_by === '0' || leave.approved_by === 0 || !leave.approved_by) ? 'Pending' : leave.approved_by,
        submittedOn: leave.created_at,
        reason: leave.reason
      })).sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn));
      
      // Calculate personal balances
      const balances = {};
      leaveTypes.value.forEach(type => {
        const total = type.default_days || 21; 
        const used = leaveHistory.value.filter(l => l.type === type.name && l.status === 'approved').reduce((sum, l) => sum + l.days, 0);
        balances[type.name] = { total, remaining: Math.max(0, total - used), lastUsed: 'Never' };
      });
      leaveBalances.value = balances;
    }
  } catch (error) {
    console.error('Error fetching leave data:', error);
  }
};

// Methods
const selectEmployee = (employee) => {
  selectedEmployee.value = employee;
  employeeSearch.value = employee.full_name || `${employee.first_name} ${employee.last_name}`;
};

const openNewRequestModal = () => {
  newRequest.value = { selectedEmployee: null, leave_type_id: '', start_date: '', end_date: '', days: 0, reason: '' };
  showNewRequestModal.value = true;
};

const calculateDuration = () => {
  if (newRequest.value.start_date && newRequest.value.end_date) {
    newRequest.value.days = calculateDays(newRequest.value.start_date, newRequest.value.end_date);
  }
};

const calculateDays = (start, end) => {
  if (!start || !end) return 0;
  return Math.ceil(Math.abs(new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) + 1;
};

const submitRequest = async () => {
  if (isHOD.value) {
    if (!newRequest.value.selectedEmployee || !newRequest.value.leave_type_id || !newRequest.value.start_date || !newRequest.value.end_date || !newRequest.value.reason) {
      alert('Please fill in all required fields and select an employee.');
      return;
    }
  } else {
    if (!newRequest.value.leave_type_id || !newRequest.value.start_date || !newRequest.value.end_date || !newRequest.value.reason) {
      alert('Please fill in all required fields.');
      return;
    }
  }
  
  isSubmitting.value = true;
  try {
    const payload = {
      user_id: currentUserId.value,
      leave_type_id: newRequest.value.leave_type_id,
      start_date: newRequest.value.start_date,
      end_date: newRequest.value.end_date,
      reason: newRequest.value.reason
    };
    
    // If HOD is submitting on behalf of an employee, pass the target applicant_id
    if (isHOD.value && newRequest.value.selectedEmployee) {
      payload.applicant_id = newRequest.value.selectedEmployee.applicant_id;
    }

    const response = await api.post('EmployeeLeaves.php', payload);

    if (response.data.success) {
      alert('Leave request submitted successfully!');
      showNewRequestModal.value = false;
      fetchData();
    } else {
      alert(response.data.message || 'Failed to submit request.');
    }
  } catch (error) {
    console.error('Submit error:', error);
    alert('An error occurred while submitting the request.');
  } finally {
    isSubmitting.value = false;
  }
};

const viewLeaveDetails = (request) => { selectedLeave.value = request; };

const updateLeaveStatus = async (id, status) => {
  if (!confirm(`Are you sure you want to ${status} this request?`)) return;
  try {
    const response = await api.post('EmployeeLeaves.php', {
      action: 'update_status',
      id: id,
      status: status,
      approved_by: currentUser.value?.name || 'HOD'
    }, { method: 'PUT' }); // Using PUT for status updates
    
    if (response.data.success) {
      alert(`Request ${status} successfully!`);
      selectedLeave.value = null;
      fetchData();
    } else {
      alert(response.data.message || 'Failed to update status.');
    }
  } catch (error) {
    console.error('Update error:', error);
    alert('An error occurred while updating the request.');
  }
};

const cancelRequest = async (id) => {
  if (!confirm('Are you sure you want to cancel this pending request?')) return;
  try {
    const response = await api.post('EmployeeLeaves.php', { user_id: currentUserId.value, id: id }, { method: 'DELETE' });
    if (response.data.success) {
      alert('Request cancelled successfully.');
      selectedLeave.value = null;
      fetchData();
    }
  } catch (error) {
    console.error('Cancel error:', error);
  }
};

// HOD Pending Requests (only those waiting for HOD approval)
const hodPendingRequests = computed(() => {
  return enrichedLeaves.value
    .filter(l => l.status === 'pending' && l.current_step === 'hod')
    .sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn));
});
const getStatusClass = (status) => {
  const map = {
    pending: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    approved: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    rejected: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  };
  return map[status] || 'bg-gray-50 text-gray-700';
};

const getLeaveTypeColor = (type) => {
  if (!type) return '#6b7280';
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = type.charCodeAt(i) + ((hash << 5) - hash);
  const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return "G";
  const parts = name.trim().split(" ");
  return parts.length === 1 ? parts[0].charAt(0).toUpperCase() : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  document.documentElement.classList.toggle('dark', isDarkMode.value);
  localStorage.theme = isDarkMode.value ? 'dark' : 'light';
};

onMounted(() => {
  if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true;
    document.documentElement.classList.add('dark');
  }
  fetchData();
});
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.multiselect-custom .multiselect__tags { background-color: white; border: 1px solid #d1d5db; border-radius: 0.5rem; min-height: 42px; padding: 0.5rem 0.75rem; }
.dark .multiselect-custom .multiselect__tags { background-color: #374151; border-color: #4b5563; color: white; }
.multiselect-custom .multiselect__input, .multiselect-custom .multiselect__single { background: transparent; color: #111827; margin-top: 2px; }
.dark .multiselect-custom .multiselect__input, .dark .multiselect-custom .multiselect__single { color: white; }
.multiselect-custom .multiselect__placeholder { color: #9ca3af; }
.multiselect-custom .multiselect__content-wrapper { background-color: white; border: 1px solid #e5e7eb; border-radius: 0.5rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
.dark .multiselect-custom .multiselect__content-wrapper { background-color: #1f2937; border-color: #374151; }
.multiselect-custom .multiselect__element span { color: #111827; }
.dark .multiselect-custom .multiselect__element span { color: white; }
.multiselect-custom .multiselect__option--highlight { background: #035a76 !important; color: white !important; }
.multiselect-custom .multiselect__option--selected { background: rgba(3, 90, 118, 0.1); color: #035a76; font-weight: 600; }
.dark .multiselect-custom .multiselect__option--selected { background: rgba(3, 90, 118, 0.2); color: #60a5fa; }
</style>
<style scoped>
.animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.modal-enter-active, .modal-leave-active { transition: all 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; transform: scale(0.95); }
</style>